/**
 * CommercialAssumptionsTab - Financial Modeling Assumptions
 *
 * Input tab for commercial/financial parameters that drive NPV,
 * payback, and lifecycle cost calculations.
 */

import React from 'react';
import {
    Box, Card, CardContent, Typography, Table, TableHead, TableBody,
    TableRow, TableCell, TextField, Alert, Tooltip, IconButton,
    Switch, FormControlLabel, Select, MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Info as InfoIcon, Restore as RestoreIcon } from '@mui/icons-material';
import { vortxColors, FINANCIAL_DEFAULTS } from '@vortx/shared-ui';

function CommercialAssumptionsTab({
    commercialAssumptions,
    readOnly = false
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const {
        analysisYears,
        setAnalysisYears,
        discountRate,
        setDiscountRate,
        taxRate,
        setTaxRate,
        degradationRate,
        setDegradationRate,
        escalationRates,
        setEscalationRates,
        taxIncentives,
        setTaxIncentives,
        resetToDefaults
    } = commercialAssumptions;

    const cardStyle = {
        bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    };

    const headerStyle = {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: vortxColors.gold }}>
                    Commercial Assumptions
                </Typography>
                {!readOnly && (
                    <Tooltip title="Reset all to defaults">
                        <IconButton onClick={resetToDefaults} size="small">
                            <RestoreIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                These parameters drive all financial calculations including NPV, payback period, and lifecycle costs.
                Values are used consistently across the Budget tab, Results, and exported reports.
            </Alert>

            {/* Project Parameters */}
            <Card sx={{ ...cardStyle, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Project Parameters
                    </Typography>

                    <Table size="small">
                        <TableHead>
                            <TableRow sx={headerStyle}>
                                <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Analysis Period */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    Analysis Period
                                    <Tooltip title={FINANCIAL_DEFAULTS.analysisYears.source}>
                                        <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Project lifecycle for NPV and cash flow analysis
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{analysisYears} years</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={analysisYears}
                                            onChange={(e) => setAnalysisYears(parseInt(e.target.value) || 20)}
                                            size="small"
                                            inputProps={{ min: 5, max: 40, style: { textAlign: 'right', width: 40 } }}
                                            sx={{ width: 120 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>years</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Discount Rate */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    Discount Rate
                                    <Tooltip title={FINANCIAL_DEFAULTS.discountRate.source}>
                                        <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Weighted average cost of capital (WACC) for NPV
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{discountRate}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={discountRate}
                                            onChange={(e) => setDiscountRate(parseFloat(e.target.value) || 8)}
                                            size="small"
                                            inputProps={{ step: 0.5, min: 0, max: 25, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Tax Rate */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    Tax Rate
                                    <Tooltip title={FINANCIAL_DEFAULTS.taxRate.source}>
                                        <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Combined federal/state corporate tax rate
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{taxRate}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={taxRate}
                                            onChange={(e) => setTaxRate(parseFloat(e.target.value) || 25)}
                                            size="small"
                                            inputProps={{ step: 1, min: 0, max: 50, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Degradation Rate */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    PV Degradation
                                    <Tooltip title={FINANCIAL_DEFAULTS.degradationRate.source}>
                                        <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Annual solar panel output degradation
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{degradationRate}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={degradationRate}
                                            onChange={(e) => setDegradationRate(parseFloat(e.target.value) || 0.5)}
                                            size="small"
                                            inputProps={{ step: 0.1, min: 0, max: 2, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%/yr</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Tax Incentives */}
            <Card sx={{ ...cardStyle, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Tax Incentives
                    </Typography>

                    <Table size="small">
                        <TableHead>
                            <TableRow sx={headerStyle}>
                                <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* MACRS Toggle */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    MACRS Depreciation
                                    <Tooltip title="Modified Accelerated Cost Recovery System - federal tax depreciation for commercial solar/storage assets">
                                        <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Include depreciation tax shield in cashflows
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{taxIncentives?.includeMacrs ? 'Enabled' : 'Disabled'}</Typography>
                                    ) : (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={taxIncentives?.includeMacrs || false}
                                                    onChange={(e) => setTaxIncentives(prev => ({
                                                        ...prev,
                                                        includeMacrs: e.target.checked
                                                    }))}
                                                    size="small"
                                                />
                                            }
                                            label={taxIncentives?.includeMacrs ? 'On' : 'Off'}
                                            sx={{ mr: 0 }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* MACRS Schedule - only shown when MACRS is enabled */}
                            {taxIncentives?.includeMacrs && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 500, pl: 4 }}>
                                        MACRS Schedule
                                        <Tooltip title="5-year: Solar, battery storage (IRS). 7-year: Other qualifying equipment">
                                            <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            IRS depreciation schedule for asset class
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {readOnly ? (
                                            <Typography>{taxIncentives?.macrsSchedule}</Typography>
                                        ) : (
                                            <Select
                                                value={taxIncentives?.macrsSchedule || '5-year'}
                                                onChange={(e) => setTaxIncentives(prev => ({
                                                    ...prev,
                                                    macrsSchedule: e.target.value
                                                }))}
                                                size="small"
                                                sx={{ minWidth: 120 }}
                                            >
                                                <MenuItem value="5-year">5-Year</MenuItem>
                                                <MenuItem value="7-year">7-Year</MenuItem>
                                            </Select>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* MACRS Basis Reduction - only shown when MACRS is enabled */}
                            {taxIncentives?.includeMacrs && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 500, pl: 4 }}>
                                        ITC Basis Reduction
                                        <Tooltip title="IRC Section 50(c): Depreciable basis is reduced by this percentage of the ITC amount. Default 50% for ITC taken after 2017.">
                                            <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            Reduce MACRS depreciable basis by % of ITC
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {readOnly ? (
                                            <Typography>{taxIncentives?.macrsBasisReductionPercent}%</Typography>
                                        ) : (
                                            <TextField
                                                type="number"
                                                value={taxIncentives?.macrsBasisReductionPercent ?? 50}
                                                onChange={(e) => setTaxIncentives(prev => ({
                                                    ...prev,
                                                    macrsBasisReductionPercent: parseFloat(e.target.value) || 0
                                                }))}
                                                size="small"
                                                inputProps={{ step: 5, min: 0, max: 100, style: { textAlign: 'right' } }}
                                                sx={{ width: 100 }}
                                                InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* ITC Treatment */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    ITC Treatment
                                    <Tooltip title="How the Investment Tax Credit is modeled in cashflows. 'Upfront reduction' reduces initial cost. 'Year 1 credit' shows as Year 1 cashflow. 'Transfer/sale' models ITC transferability at a discount.">
                                        <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        How ITC flows into the financial model
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>
                                            {taxIncentives?.itcTreatment === 'upfront_reduction' ? 'Upfront Reduction' :
                                             taxIncentives?.itcTreatment === 'year_one_credit' ? 'Year 1 Tax Credit' :
                                             taxIncentives?.itcTreatment === 'transfer' ? 'Transfer/Sale' : '—'}
                                        </Typography>
                                    ) : (
                                        <Select
                                            value={taxIncentives?.itcTreatment || 'upfront_reduction'}
                                            onChange={(e) => setTaxIncentives(prev => ({
                                                ...prev,
                                                itcTreatment: e.target.value
                                            }))}
                                            size="small"
                                            sx={{ minWidth: 180 }}
                                        >
                                            <MenuItem value="upfront_reduction">Upfront Reduction</MenuItem>
                                            <MenuItem value="year_one_credit">Year 1 Tax Credit</MenuItem>
                                            <MenuItem value="transfer">Transfer / Sale</MenuItem>
                                        </Select>
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* ITC Transfer Discount - only shown when transfer mode */}
                            {taxIncentives?.itcTreatment === 'transfer' && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 500, pl: 4 }}>
                                        Transfer Discount
                                        <Tooltip title="ITC transferability discount: the buyer typically pays less than face value. E.g., 10% discount means you receive 90 cents per dollar of ITC.">
                                            <InfoIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, cursor: 'help' }} />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            Haircut on ITC value when selling/transferring
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {readOnly ? (
                                            <Typography>{taxIncentives?.itcTransferDiscountPercent}%</Typography>
                                        ) : (
                                            <TextField
                                                type="number"
                                                value={taxIncentives?.itcTransferDiscountPercent ?? 10}
                                                onChange={(e) => setTaxIncentives(prev => ({
                                                    ...prev,
                                                    itcTransferDiscountPercent: parseFloat(e.target.value) || 0
                                                }))}
                                                size="small"
                                                inputProps={{ step: 1, min: 0, max: 50, style: { textAlign: 'right' } }}
                                                sx={{ width: 100 }}
                                                InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Escalation Rates */}
            <Card sx={cardStyle}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Annual Escalation Rates
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Annual cost increase rates applied to {analysisYears}-year lifecycle calculations
                    </Typography>

                    <Table size="small">
                        <TableHead>
                            <TableRow sx={headerStyle}>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Application</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Annual Rate</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Energy */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Energy</TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Utility energy charges ($/kWh)
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{escalationRates.energy}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={escalationRates.energy}
                                            onChange={(e) => setEscalationRates(prev => ({
                                                ...prev,
                                                energy: parseFloat(e.target.value) || 0
                                            }))}
                                            size="small"
                                            inputProps={{ step: 0.5, min: 0, max: 15, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Demand */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Demand</TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Utility demand charges ($/kW)
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{escalationRates.demand}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={escalationRates.demand}
                                            onChange={(e) => setEscalationRates(prev => ({
                                                ...prev,
                                                demand: parseFloat(e.target.value) || 0
                                            }))}
                                            size="small"
                                            inputProps={{ step: 0.5, min: 0, max: 15, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Natural Gas - Delivery */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Natural Gas - Delivery</TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Gas delivery charges (typically tracks inflation)
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{escalationRates.naturalGasDelivery}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={escalationRates.naturalGasDelivery}
                                            onChange={(e) => setEscalationRates(prev => ({
                                                ...prev,
                                                naturalGasDelivery: parseFloat(e.target.value) || 0
                                            }))}
                                            size="small"
                                            inputProps={{ step: 0.5, min: 0, max: 15, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Natural Gas - Commodity */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Natural Gas - Commodity</TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        Gas commodity costs (more volatile, market-driven)
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{escalationRates.naturalGasCommodity}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={escalationRates.naturalGasCommodity}
                                            onChange={(e) => setEscalationRates(prev => ({
                                                ...prev,
                                                naturalGasCommodity: parseFloat(e.target.value) || 0
                                            }))}
                                            size="small"
                                            inputProps={{ step: 0.5, min: 0, max: 15, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Labor */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Labor</TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        O&M and service costs
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {readOnly ? (
                                        <Typography>{escalationRates.labor}%</Typography>
                                    ) : (
                                        <TextField
                                            type="number"
                                            value={escalationRates.labor}
                                            onChange={(e) => setEscalationRates(prev => ({
                                                ...prev,
                                                labor: parseFloat(e.target.value) || 0
                                            }))}
                                            size="small"
                                            inputProps={{ step: 0.5, min: 0, max: 15, style: { textAlign: 'right' } }}
                                            sx={{ width: 100 }}
                                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>%</Typography> }}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
}

export default CommercialAssumptionsTab;
